import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Linkedin, Mail, Home } from 'lucide-react';

export function Navigation() {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isBlogPage = location.pathname.startsWith('/blog');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hasSolidBar = isScrolled || isBlogPage;

  const navLinks = [
    { href: '#about', label: 'About' },
    { href: '#dimensions', label: 'Dimensions' },
    { href: '#experience', label: 'Experience' },
    { href: '#blog', label: 'Blog' },
    { href: '#connect', label: 'Connect' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        hasSolidBar
          ? 'bg-white/90 backdrop-blur-xl border-b border-border shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className={`font-heading text-2xl font-bold transition-colors ${
              hasSolidBar ? 'text-primary hover:text-secondary' : 'text-[#8B4513] hover:text-[#C9A227]'
            }`}
          >
            SK
          </Link>

          {/* Desktop Navigation: section links on home only */}
          {isHomePage && (
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`text-sm font-body transition-colors uppercase tracking-widest ${
                    hasSolidBar
                      ? 'text-muted-foreground hover:text-primary'
                      : 'text-[#5D4E37] hover:text-[#C9A227]'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}

          {/* Right: "Back to home" (on blog/other) + Social Links */}
          <div className="hidden md:flex items-center gap-6">
            {!isHomePage && (
              <Link
                to="/"
                data-testid="nav-back-to-home"
                className={`inline-flex items-center justify-center w-8 h-8 transition-colors ${
                  hasSolidBar
                    ? 'text-muted-foreground hover:text-primary'
                    : 'text-[#5D4E37] hover:text-[#C9A227]'
                }`}
                aria-label="Back to home"
              >
                <Home size={20} />
              </Link>
            )}
            <a
              href="https://www.linkedin.com/in/sachin-k-9806b2189/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-linkedin"
              className={`transition-colors ${
                hasSolidBar ? 'text-muted-foreground hover:text-primary' : 'text-[#5D4E37] hover:text-[#C9A227]'
              }`}
            >
              <Linkedin size={20} />
            </a>
            <a
              href="mailto:sachin.kothamasa@gmail.com"
              data-testid="nav-email"
              className={`transition-colors ${
                hasSolidBar ? 'text-muted-foreground hover:text-primary' : 'text-[#5D4E37] hover:text-[#C9A227]'
              }`}
            >
              <Mail size={20} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className={`md:hidden ${hasSolidBar ? 'text-foreground' : 'text-[#5D4E37]'}`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-border"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {isHomePage ? (
                navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-lg font-body text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                ))
              ) : (
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Back to home"
                >
                  <Home size={24} />
                </Link>
              )}
              <div className="flex gap-6 pt-4 border-t border-border">
                <a
                  href="https://www.linkedin.com/in/sachin-k-9806b2189/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:sachin.kothamasa@gmail.com"
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
